import React, { useRef, useState } from 'react';
import {
  useFloating,
  size,
  offset,
  autoPlacement,
  hide as hideMiddleware,
  autoUpdate,
  useInteractions,
  useClick,
  useListNavigation,
  useDismiss,
  useRole,
} from '@floating-ui/react';
import { AriaFocusProps } from '../../types/common';
import classNames from 'classnames';
import { IconDotsVertical, IconProps } from '../icons/generated';
import { Button, ButtonProps } from './Button';
import { DropdownOptions } from '../dropdown/DropdownOptions';
import { DEFAULT_DROPDOWN_MAX_HEIGHT } from '../dropdown/constants';
import { IconButton } from './IconButton';
import { ButtonKind, IconButtonKind } from './types';
import { DropdownOption } from '../dropdown/types';

export interface PopoverButtonProps<OptionValue> extends Omit<ButtonProps, 'size' | 'value' | 'onChange' | 'kind'> {
  /** @default false */
  useIconButton?: boolean;
  /** @default 'medium' */
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  /**
   * floating 요소가 reference 영역을 넘어갈 경우 자동 위치 처리 사용 유무
   *  - 자동 위치 상/하만 적용됩니다.
   * @default true
   */
  autoPlacement?: boolean;
  /** @default getZIndex('dropdown') */
  zIndex?: number;
  /** @default true */
  optionsMatchRefWidth?: boolean;
  /**
   * Popover OptionList maxHeight값
   * @default DEFAULT_DROPDOWN_MAX_HEIGHT(400)
   */
  maxHeight?: number;
  disabled?: boolean;
  value?: OptionValue;
  /**
   * IconButton과 동시 지원을 위해 IconButtonKind로 제한합니다.
   * @default 'outlined_secondary'
   * */
  kind?: ButtonKind;
  icon?: React.ReactElement<IconProps, string | React.JSXElementConstructor<unknown>>;
  onChange?: (value: OptionValue) => void;
  options: Array<DropdownOption<OptionValue>>;
  /**
   * 커스텀 버튼 렌더링 함수
   */
  renderButton?: ({
    buttonProps,
  }: {
    buttonProps: { onClick: (e: React.MouseEvent) => void } & Record<string, unknown>;
  }) => React.ReactNode;
}

function isOutlinedKind(kind: ButtonKind): kind is IconButtonKind {
  return kind === 'outlined_primary' || kind === 'outlined_secondary' || kind === 'outlined_negative';
}

export const PopoverButton = <OptionValue extends string | number>({
  useIconButton,
  size: sizeProp = 'medium',
  loading,
  type,
  kind = 'outlined_secondary',
  icon,
  startIcon,
  className,
  children,
  zIndex,
  autoPlacement: autoPlacementProps = true,
  optionsMatchRefWidth = false,
  options,
  value: valueProp,
  maxHeight = DEFAULT_DROPDOWN_MAX_HEIGHT,
  disabled,
  unstableFocusVisible,
  onChange,
  renderButton,
}: PopoverButtonProps<OptionValue> & AriaFocusProps) => {
  const [opened, setOpened] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const optionListRef = useRef<Array<HTMLElement | null>>([]);
  const selectedIndex = options.findIndex(({ value }) => value === valueProp);
  const { refs, floatingStyles, context } = useFloating({
    open: opened,
    onOpenChange: setOpened,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      size({
        apply({ rects, elements, availableWidth, availableHeight }) {
          Object.assign(elements.floating.style, {
            width: optionsMatchRefWidth ? `${rects.reference.width}px` : undefined,
            maxWidth: !optionsMatchRefWidth ? `${availableWidth}px` : undefined,
            maxHeight: `${availableHeight < maxHeight ? availableHeight - 20 : maxHeight}px`,
          });
        },
      }),
      ...(autoPlacementProps
        ? [autoPlacement({ allowedPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'], padding: 4 })]
        : []),
      hideMiddleware(),
    ],
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    useClick(context, { enabled: !disabled, event: 'mousedown' }),
    useDismiss(context),
    useRole(context, { role: 'listbox' }),
    useListNavigation(context, {
      listRef: optionListRef,
      activeIndex: focusIndex,
      selectedIndex,
      onNavigate: setFocusIndex,
    }),
  ]);

  const sharedButtonProps = {
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpened(false);
    },
    ...getReferenceProps({
      ref: refs.setReference,
      className: classNames('pds-dropdown-label', className),
      disabled,
      selected: selectedIndex !== -1,
      tabIndex: 0,
    }),
  };

  const buttonProps = {
    type,
    size: sizeProp,
    disabled,
    ...(useIconButton
      ? {
          icon: icon || <IconDotsVertical />,
        }
      : {
          loading,
          startIcon: startIcon,
          endIcon: <IconDotsVertical />,
          children,
        }),
    ...sharedButtonProps,
  };

  return (
    <>
      {typeof renderButton === 'function' ? (
        renderButton({ buttonProps: sharedButtonProps })
      ) : useIconButton ? (
        <IconButton kind={isOutlinedKind(kind) ? kind : undefined} {...buttonProps} />
      ) : (
        <Button kind={kind} {...buttonProps} />
      )}
      <DropdownOptions
        refs={refs}
        setRefs={(node, index) => {
          optionListRef.current[index] = node;
        }}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        getItemProps={getItemProps}
        onClose={() => {
          setOpened(false);
          setFocusIndex(null);
        }}
        size={sizeProp}
        zIndex={zIndex}
        unstableFocusVisible={unstableFocusVisible}
        context={context}
        onChange={onChange}
        focusIndex={focusIndex}
        options={options}
      />
    </>
  );
};
